import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'
import { haveSameTagIds } from '@/helpers/file'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  libraryAgencyQueryKeys,
  libraryCatalogEntriesQueryKeys,
  libraryCompanyQueryKeys,
  libraryCompaniesQueryKeys,
  libraryDepartmentQueryKeys,
  libraryDepartmentsQueryKeys,
  libraryEntitiesQueryKeys,
  libraryPeopleQueryKeys,
} from '@/hooks/library/types'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { Entity, EntityType } from '@/types/library/Entity'

interface UpdateEntityTagsVariables {
  entityId: UUID
  entityType: EntityType
  tagIds: UUID[]
}

interface UseEntityTagComboboxArgs {
  entity: Entity
}

const getEntityTagIds = (entity: Entity): UUID[] => entity.tag_ids ?? []

const updateEntityTags = async (
  orgId: UUID,
  accessToken: string | undefined | null,
  variables: UpdateEntityTagsVariables,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${variables.entityId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        entity_type: variables.entityType,
        tag_ids: variables.tagIds,
      }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

export const useUpdateEntityTags = (orgId: UUID | '') => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  const handleMutationError = useCallback(
    (error: Error) => {
      handleError(error)
    },
    [handleError],
  )

  return useMutation<void, Error, UpdateEntityTagsVariables>({
    mutationFn: (variables) =>
      updateEntityTags(orgId as UUID, accessToken, variables),
    onError: handleMutationError,
    onSuccess: async (_data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: libraryEntitiesQueryKeys.organization({ orgId }),
        }),
      ]

      if (variables.entityType === 'company') {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: libraryCompanyQueryKeys.company({
              orgId,
              companyId: variables.entityId,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: libraryCompaniesQueryKeys.organization({ orgId }),
          }),
        )
      }

      if (variables.entityType === 'person') {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: libraryPeopleQueryKeys.person({
              orgId,
              personId: variables.entityId,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: libraryPeopleQueryKeys.organization({ orgId }),
          }),
        )
      }

      if (variables.entityType === 'agency') {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: libraryAgencyQueryKeys.agency({
              orgId,
              agencyId: variables.entityId,
            }),
          }),
          // Invalidate every list variant — keying on withConfirmed/withSuggested
          // makes positional prefix-matching unreliable for a partial key.
          queryClient.invalidateQueries({
            queryKey: libraryAgencyQueryKeys.all(),
          }),
        )
      }

      if (variables.entityType === 'catalog_entry') {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: libraryCatalogEntriesQueryKeys.entry({
              orgId,
              catalogEntryId: variables.entityId,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: libraryCatalogEntriesQueryKeys.organization({ orgId }),
          }),
        )
      }

      if (variables.entityType === 'department') {
        // Department list/single keys are scoped by company, which we don't
        // have here — invalidate by org-level prefix to catch all of them.
        const matchesDepartment = libraryDepartmentQueryKeys.matchesAnyCompany({
          orgId,
          departmentId: variables.entityId,
        })
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: libraryDepartmentsQueryKeys.organization({ orgId }),
          }),
          queryClient.invalidateQueries({
            predicate: (query) => matchesDepartment(query.queryKey),
          }),
        )
      }

      await Promise.all(invalidations)
    },
  })
}

export const useEntityTagCombobox = ({ entity }: UseEntityTagComboboxArgs) => {
  const { activeOrgId } = useActiveOrgId()
  const entityTagIds = useMemo(() => getEntityTagIds(entity), [entity.tag_ids])
  const { mutateAsync: persistEntityTags, isPending: isSaving } =
    useUpdateEntityTags(activeOrgId)
  const [comboboxOpen, setComboboxOpen] = useState<boolean>(false)
  const [selectedTagIds, setSelectedTagIds] = useState<UUID[]>(() => [
    ...entityTagIds,
  ])

  const hasPendingTagChanges = !haveSameTagIds(selectedTagIds, entityTagIds)
  const isClearSelected = hasPendingTagChanges && selectedTagIds.length === 0

  const resetSelectedTagIds = useCallback(() => {
    setSelectedTagIds([...entityTagIds])
  }, [entityTagIds])

  const handleSelectionChange = useCallback((tagIds: string[] | null) => {
    setSelectedTagIds(tagIds ? [...(tagIds as UUID[])] : [])
  }, [])

  const handleToggleTag = useCallback((tagId: UUID) => {
    setSelectedTagIds((currentTagIds) =>
      currentTagIds.includes(tagId)
        ? currentTagIds.filter((currentTagId) => currentTagId !== tagId)
        : [...currentTagIds, tagId],
    )
  }, [])

  const persistSelectedTagIds = useCallback(async () => {
    if (haveSameTagIds(selectedTagIds, entityTagIds)) {
      return
    }

    await persistEntityTags({
      entityId: entity.id,
      entityType: entity.entity_type,
      tagIds: selectedTagIds,
    })
    setSelectedTagIds([...selectedTagIds])
    toast.success(
      selectedTagIds.length === 0
        ? 'Entity tags cleared.'
        : 'Entity tags updated.',
    )
  }, [
    entity.id,
    entity.entity_type,
    entityTagIds,
    persistEntityTags,
    selectedTagIds,
  ])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setComboboxOpen(open)

      if (!open) {
        resetSelectedTagIds()
      }
    },
    [resetSelectedTagIds],
  )

  const handleCancel = useCallback(() => {
    resetSelectedTagIds()
    setComboboxOpen(false)
  }, [resetSelectedTagIds])

  const handleClear = useCallback(() => {
    setSelectedTagIds([])
  }, [])

  const handleSave = useCallback(async (): Promise<boolean> => {
    try {
      await persistSelectedTagIds()
      setComboboxOpen(false)
      return true
    } catch {
      // errors are handled by the mutation hook
      return false
    }
  }, [persistSelectedTagIds])

  useEffect(() => {
    resetSelectedTagIds()
  }, [resetSelectedTagIds])

  return {
    comboboxOpen,
    selectedTagIds,
    hasPendingTagChanges,
    isClearSelected,
    isSaving,
    handleOpenChange,
    handleSelectionChange,
    handleToggleTag,
    handleCancel,
    handleClear,
    handleSave,
  }
}
